/**
 * Character3DThumbnail — a drop-in replacement for {@link IconAvatar} in DENSE
 * tiles (lobby seats, the Hall of Fame leaderboard, the room browser) that shows
 * the premium 3D character instead of the flat 2D portrait — WITHOUT a live
 * WebGL context per tile.
 *
 * It renders the cheap 2D `IconAvatar` immediately (the fallback), and in an
 * effect asks the shared offscreen renderer ({@link getCharacterThumbnail}) for
 * a cached 3D image of this icon. The heavy renderer (Three.js + @tt/shell-3d)
 * is reached ONLY through a dynamic `import()` here, so Three stays out of the
 * main bundle until a screen with these tiles actually mounts. When the data URL
 * resolves it swaps to an `<img>`; if WebGL is unavailable or the render fails
 * the component simply stays on the 2D `IconAvatar` (never a black/empty tile).
 *
 * The image is a STATIC PNG, so it is inherently reduced-motion friendly and
 * costs nothing per frame. Many tiles wearing the same character share ONE
 * cached image (keyed by the icon's cosmetic signature).
 */
import { useEffect, useState } from 'react';
import type { CharacterIcon } from '@tt/types';
import { IconAvatar, type IconValue } from './IconAvatar';
import type { ThumbFrame } from './character3d-thumbnail';

type ThumbModule = typeof import('./character3d-thumbnail');

// Module-cached dynamic import so every tile shares ONE chunk load. The bundler
// also de-dupes this, but memoising avoids re-creating the promise per mount.
let modPromise: Promise<ThumbModule> | null = null;
function loadThumbModule(): Promise<ThumbModule> {
  if (!modPromise) modPromise = import('./character3d-thumbnail');
  return modPromise;
}

interface Props {
  icon: IconValue;
  /** Square edge in CSS px. Default 64. */
  size?: number;
  alt?: string | undefined;
  /** Clip to a circle (matches IconAvatar's round portrait). Default true. */
  circular?: boolean;
  /**
   * Framing — `portrait` (head-and-shoulders; the default for dense seat /
   * leaderboard tiles) or `figure` (full head-to-feet WITH the showcase
   * backdrop + flair staged around the figure). Use `figure` for the big
   * lobby identity tile + the profile pic so the player's backdrop, aura,
   * particles, and glow read.
   */
  frame?: ThumbFrame;
  className?: string;
}

/** Stable key for an icon's full appearance — mirrors the fields @tt/shell-3d's
 *  `iconHash` keys on. Computed locally so this light component doesn't import
 *  the Three-laden shell module just to hash. Includes the Flair cosmetics
 *  (outfit / particles / rimHue) so the effect re-fetches when they change —
 *  the showcase figure frame stages those, and omitting them left a stale image
 *  after equipping a backdrop / particles / glow. */
function iconKey(icon: IconValue): string {
  return [
    icon.set, icon.id, icon.hue,
    icon.body ?? -1, icon.hat ?? -1, icon.face ?? -1, icon.accentHue ?? -1,
    icon.paletteId ?? '', icon.border ?? '', icon.background ?? '', icon.mask ?? '',
    icon.outfit ?? '', icon.particles ?? '', icon.rimHue ?? -1,
  ].join('|');
}

export function Character3DThumbnail({ icon, size = 64, alt, circular = true, frame = 'portrait', className }: Props) {
  const [url, setUrl] = useState<string | null>(null);
  const key = `${frame}|${iconKey(icon)}`;

  useEffect(() => {
    let alive = true;
    setUrl(null); // appearance changed — drop the stale image, show 2D meanwhile
    loadThumbModule()
      .then((m) => {
        const peeked = m.peekCharacterThumbnail(icon as CharacterIcon, frame);
        if (peeked) {
          if (alive) setUrl(peeked);
          return undefined;
        }
        return m.getCharacterThumbnail(icon as CharacterIcon, frame).then((u) => {
          if (alive && u) setUrl(u);
        });
      })
      .catch(() => {
        /* heavy chunk failed to load — keep the 2D fallback */
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Fallback: the original flat portrait while the thumbnail renders, or
  // permanently if WebGL is unavailable.
  if (!url) {
    return <IconAvatar icon={icon} size={size} {...(alt !== undefined ? { alt } : {})} />;
  }

  return (
    <span
      className={className}
      role={alt ? 'img' : undefined}
      aria-label={alt}
      style={{
        width: size,
        height: size,
        display: 'inline-block',
        borderRadius: circular ? '50%' : 12,
        overflow: 'hidden',
        flex: '0 0 auto',
        // Warm tavern-ember backdrop behind the rounded corners (the PNG is
        // opaque, so this only shows at the clipped edges).
        background:
          'radial-gradient(ellipse at 50% 38%, rgba(58,40,26,0.55) 0%, rgba(13,10,20,0.9) 100%)',
      }}
    >
      <img
        src={url}
        width={size}
        height={size}
        alt=""
        aria-hidden="true"
        style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
      />
    </span>
  );
}
